// RotationDialog.jsx
// This component provides a dialog for rotating and mirroring images before importing them.

// Copyright HS Analysis GmbH, 2025
// Author: Viktor Eberhardt

// Framework imports
import React, { useState, useEffect } from "react";

// MUI imports
import { makeStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";

// HSA imports
import Backend from "../../common/utils/Backend";

const useStyles = makeStyles(() => ({
  root: {
    marginBottom: 16,
  },
  previewContainer: {
    position: "relative",
    width: 400,
    height: 400,
    marginTop: 16,
    overflow: "hidden",
    background: "#fafafa",
    borderRadius: 8,
    border: "1px solid #eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  topContent: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  previewImage: {
    position: "absolute",
    top: "50%",
    left: "50%",
    maxWidth: "80%",
    maxHeight: "80%",
    display: "block",
  },
}));

function RotationDialog({
  open,
  onClose,
  onConfirm,
  src,
  fileId,
  ome,
  rotState,
}) {
  const classes = useStyles();
  const [angle, setAngle] = useState(0);
  const [mirrored, setMirrored] = useState(false);
  const [dragging, setDragging] = useState(false); // Track slider drag
  const [usageInfo, setUsageInfo] = useState(null);

  // Log when the dialog is opened. Include fileId if available.
  useEffect(() => {
    if (!open) return;
    // 1) Load usage info (warning section)
    if (fileId) {
      Backend.getFileUsageInfo(fileId).then((info) => {
        setUsageInfo(info);
      });
    }

    // 2) Preset angle/mirror from metadata and renderer state
    // Angle = ome.angle + rotState; normalize to [-180, 180]
    const normalizeSigned = (deg) => {
      const raw = ((deg % 360) + 360) % 360;
      return raw > 180 ? raw - 360 : raw;
    };
    const metaAngle = typeof ome?.angle === "number" ? ome.angle : 0;
    const addRot = typeof rotState === "number" ? rotState : 0;
    const baseAngle = normalizeSigned(metaAngle + addRot);
    const baseMirrored =
      typeof ome?.mirrored === "boolean" ? ome.mirrored : false;
    setAngle(baseAngle);
    setMirrored(baseMirrored);
  }, [open, fileId, ome, rotState]);

  if (!src || !open) {
    return null;
  }

  /**
   * Handles the change event for the angle input field.
   * Updates the angle state with the numeric value from the input.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the angle input field.
   */
  const handleAngleChange = (e) => {
    setAngle(Number(e.target.value));
  };

  /**
   * Toggles the mirrored state of the component.
   * When invoked, it switches the current mirrored value between true and false.
   */
  const handleMirror = () => {
    setMirrored((prev) => !prev);
  };

  /**
   * Handles the import action by invoking the onConfirm callback with the current angle and mirrored state,
   * then closes the dialog.
   */
  const handleImport = () => {
    // Prevent no-op requests if already at this angle/mirror state
    const normalizeSigned = (deg) => {
      const raw = ((deg % 360) + 360) % 360;
      return raw > 180 ? raw - 360 : raw;
    };
    const metaAngle = typeof ome?.angle === "number" ? ome.angle : 0;
    const addRot = typeof rotState === "number" ? rotState : 0;
    const currentAbs = normalizeSigned(metaAngle + addRot);
    const baseMirrored =
      typeof ome?.mirrored === "boolean" ? ome.mirrored : false;
    if (angle === currentAbs && mirrored === baseMirrored) {
      window.showSuccessSnackbar?.(
        `File is already rotated to ${angle}°${mirrored ? ", mirrored" : ""}`
      );
      onClose();
      return;
    }

    if (onConfirm) onConfirm({ angle, mirrored });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Free Rotation</DialogTitle>
      <DialogContent>
        <div className={classes.root}>
          <div className={classes.topContent}>
            <span style={{ minWidth: 40 }}>Angle</span>
            <Slider
              value={angle}
              onChange={(_, value) => setAngle(value)}
              min={-180}
              max={180}
              step={1}
              marks={[
                { value: -180, label: "-180°" },
                { value: 0, label: "0°" },
                { value: 180, label: "180°" },
              ]}
              valueLabelDisplay="auto"
              sx={{ width: 200 }}
              onChangeCommitted={() => setDragging(false)}
              onMouseDown={() => setDragging(true)}
              onTouchStart={() => setDragging(true)}
              onMouseUp={() => setDragging(false)}
              onTouchEnd={() => setDragging(false)}
            />
            <TextField
              label="Angle"
              type="number"
              value={angle}
              onChange={handleAngleChange}
              inputProps={{
                step: 1,
                min: -180,
                max: 180,
                style: { width: 60 },
              }}
              size="small"
            />
            <Button
              onClick={handleMirror}
              variant={mirrored ? "contained" : "outlined"}
            >
              {mirrored ? "Mirrored" : "Mirror"}
            </Button>
          </div>
          <div className={classes.previewContainer}>
            <img
              src={src}
              alt="Rotation Preview"
              className={classes.previewImage}
              style={{
                transform: `translate(-50%, -50%) rotate(${angle}deg) scaleX(${
                  mirrored ? -1 : 1
                })`,
                transition: dragging ? "none" : "transform 0.1s",
              }}
            />
          </div>

          {usageInfo && Array.isArray(usageInfo) && usageInfo.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Alert severity="warning">
                <div>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    This file is used in other projects and has annotations.
                  </Typography>
                  <Typography variant="caption" display="block" gutterBottom>
                    Rotating the file will break the annotations.
                    <br />
                    Let the Owners of the projects know about the change.
                    <br />
                    If the file is rotated back to its original orientation, the
                    annotations will be correct again.
                    <br />
                    Affected are the following projects:
                  </Typography>
                </div>

                <List dense>
                  {usageInfo.map((u, idx) => (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            style={{
                              wordBreak: "normal",
                              overflowWrap: "anywhere",
                              hyphens: "auto",
                            }}
                          >
                            {`ID ${u.readableID} — ${u.projectName} — ${
                              u.annotationCount
                            } annotation${u.annotationCount === 1 ? "" : "s"}`}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            style={{
                              wordBreak: "normal",
                              overflowWrap: "anywhere",
                              hyphens: "auto",
                            }}
                          >
                            {`Owner: ${u.user}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            </div>
          )}

          <p>
            Rotation is applied as an absolute angle relative to the original
            image. The slider starts at the file&#39;s current rotation so you
            can adjust from there.
          </p>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleImport} variant="contained" color="primary">
          Rotate
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RotationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  src: PropTypes.string,
  fileId: PropTypes.string,
  ome: PropTypes.object,
  rotState: PropTypes.number,
};

export default RotationDialog;
